import { useCallback, useEffect, useRef, useState } from 'react'

import Head from 'next/head'

import { ease } from '@nick-mazuk/lib/animation'
import { formatNumber } from '@nick-mazuk/lib/text-styling'
import { Accordion, AccordionGroup } from '@nick-mazuk/ui/elements/accordion'
import { Box } from '@nick-mazuk/ui/elements/box'
import { Button } from '@nick-mazuk/ui/elements/button'
import { Spotify } from '@nick-mazuk/ui/elements/icon'
import { Image } from '@nick-mazuk/ui/elements/image'
import { Link } from '@nick-mazuk/ui/elements/link'
import { RealTime } from '@nick-mazuk/ui/elements/real-time'
import { ShowMore } from '@nick-mazuk/ui/elements/show-more'
import { Text } from '@nick-mazuk/ui/elements/text'
import { TextContent } from '@nick-mazuk/ui/elements/text-content'
import { Layout } from 'components/layout'
import { SpotifyTrack } from 'components/spotify-track'
import { motion } from 'framer-motion'

import type { ParsedTrack } from 'lib/spotify'
import {
    useAboutPageViews,
    useCurrentlyPlaying,
    useRecentlyPlayed,
    useTopTracks,
    useWebsiteViews,
    useYouTubeAnalytics,
} from 'lib/swr-requests'

const Bio = (): JSX.Element => {
    const [expanded, setExpanded] = useState(false)
    const [contentHeight, setContentHeight] = useState(0)
    const content = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (content.current) {
            const margin = parseFloat(
                getComputedStyle(content.current).getPropertyValue('margin-top')
            )
            setContentHeight(content.current.clientHeight + margin)
        }
    }, [])

    const contentStates = {
        expanded: { height: contentHeight },
        condensed: { height: 0 },
    }

    const handleExpand = useCallback(() => setExpanded(!expanded), [expanded])
    return (
        <div className='grid grid-cols-1 gap-12 sm:gap-16 md:grid-cols-2'>
            <div>
                <div className='sticky overflow-hidden rounded-lg shadow-xl top-32'>
                    <Image
                        src='/images/stork-tower.jpg'
                        alt='Nick Mazuk in front of Storke Tower at UCSB'
                        width={4032}
                        height={3021}
                    />
                </div>
            </div>
            <div>
                <div className='flex flex-col space-y-4'>
                    <TextContent fullwidth responsive>
                        <h1>Hi, I'm Nick</h1>
                        <p>
                            I believe people with a passion for music can change the world for the
                            better. I first craft memorable music that form emotional connections
                            with my listeners, then I help others do the same.
                        </p>
                        <motion.div
                            variants={contentStates}
                            transition={{ duration: 0.15, ease: ease['ease-in-out-quad'] }}
                            animate={expanded ? 'expanded' : 'condensed'}
                            initial='condensed'
                            className='overflow-hidden'
                        >
                            <div ref={content}>
                                <p style={{ marginTop: 0 }}>
                                    As a composer, my work has gotten many awards, including winning
                                    the Christopher Bill Composition Competition in 2018 and being a
                                    4-time Corwin Award recipient. I've done arrangements for the
                                    Division Brass Quintet, Santa Barbara Youth Symphony, Ojai Pops
                                    Orchestra, as well as the Santa Barbara Trombone Society. I deal
                                    mainly with instrumental music, and orchestra is my bread and
                                    butter.
                                </p>
                                <p>
                                    As a trombonist, I've played all around the country, including
                                    Indianapolis, Eugene, and Bellingham. I've also performed with
                                    some of the best players in the industry: Joe Alessi, Alex Iles,
                                    Bob McChesney, Bill Reichenbach, Robert Soto, James Miller, and
                                    so many more. I've also taken lessons and gotten tens of
                                    masterclasses. I do my best to play all styles of music that
                                    trombone can offer, from the most conservative Gabrielli to the
                                    incredibly modern Donald Erb, and every classical and popular
                                    style there is.
                                </p>
                                <p>
                                    Since graduating from UC Santa Barbara in 2019 with degrees in
                                    composition and trombone performance, I've been helping
                                    composers around the world create, share, and sell their music.
                                </p>
                                <p>
                                    I run{' '}
                                    <Link href='https://finalesuperuser.com'>Finale Superuser</Link>{' '}
                                    which helps teach composers how to use the industry standard
                                    music notation software. I'm a contributing author on{' '}
                                    <Link href='https://www.rpmseattle.com/of_note/author/nickmazuk/'>
                                        OF NOTE
                                    </Link>
                                    ,{' '}
                                    <Link href='https://www.scoringnotes.com/author/nick/'>
                                        Scoring Notes
                                    </Link>
                                    , the{' '}
                                    <Link href='https://www.finalemusic.com/blog/author/nmazuk/'>
                                        Finale Blog
                                    </Link>
                                    , and more, to help even more composers.
                                </p>
                                <p>
                                    Then, in 2019, I created{' '}
                                    <Link href='https://partsandscore.com/sell'>
                                        Parts and Score
                                    </Link>
                                    —the best place for composers to sell, promote, and protect
                                    their music. So far, hundreds of composers have thousands of
                                    pieces of music for sale. And they love the site.
                                </p>
                                <p>
                                    And this is just the beginning. Because I am not a conventional
                                    musician, and I don't intend to become one.
                                </p>
                            </div>
                        </motion.div>
                    </TextContent>
                    <ShowMore expanded={expanded} onClick={handleExpand} />
                </div>
            </div>
        </div>
    )
}

const CoreValues = (): JSX.Element => {
    return (
        <div className='grid grid-cols-1 gap-8 md:gap-16 md:grid-cols-2'>
            <div>
                <div className='sticky flex flex-col space-y-8 top-32'>
                    <Text h2>Core Values</Text>
                    <Text>
                        What we believe determines what we do, and therefore what we do is a
                        reflection of what we believe. These core values are what I believe and how
                        I wish to act, and you can hold me to them. Now I may make mistakes from
                        time to time, but this is what I strive for.
                    </Text>
                </div>
            </div>
            <AccordionGroup>
                <Accordion small title='Think big, then think bigger'>
                    <Text>
                        Thinking small is a self-fulfilling prophecy. Impactful solutions require
                        big changes, and big changes only come from impossibly large goals.
                    </Text>
                </Accordion>
                <Accordion small title='Insist on the highest standards'>
                    <Text>
                        Every detail matters. Best in the world isn't good enough. Only by
                        continually raising the bar is it possible to truly create innovative
                        solutions to music's largest problems.
                    </Text>
                </Accordion>
                <Accordion small title='Proactively solve hard problems'>
                    <Text>
                        Instinctively pursue and solve hard problems. Anyone can solve easy
                        problems, and change comes from solving problems no one else is willing to
                        solve in ways no one is willing to solve it. Only then can you create
                        solutions that are orders of magnitude better than everything else. Because
                        you shouldn't do things differently for the sake of being different. It
                        needs to be a whole lot better.
                    </Text>
                </Accordion>
                <Accordion small title='People come first'>
                    <Text>
                        Music is a form of self-expression. Music is how we, human beings, connect
                        with each other. Focusing on profits over people will not only hurt in the
                        long run, but is also completely contradictory towards my goals. It doesn't
                        mean not earning income, it just means people come first.
                    </Text>
                </Accordion>
                <Accordion small title='Play the long game'>
                    <Text>
                        Never sacrifice relationships and long-term goals for short-term profits.
                        This requires constantly doing what is right and not what is easy.
                    </Text>
                </Accordion>
                <Accordion small title='Be open and transparent'>
                    <Text>
                        Trust. Openness and transparency are the backbone of creating trust, and
                        nothing can be accomplished without trust. It's not always easy, but it's
                        always right.
                    </Text>
                </Accordion>
                <Accordion small title='Your actions are your real values'>
                    <Text>
                        Anyone can post their believes on their website. And they might actually
                        believe what they write. But what you do—not say—shows what you really
                        believe. Always make sure that what you do what you believe. That way, you
                        actually believe in what you do.
                    </Text>
                </Accordion>
            </AccordionGroup>
        </div>
    )
}

const Stat = ({ stat, label, url }: { stat: number | string; label: string; url?: string }) => {
    const content = (
        <Box style='outline'>
            <Text small>{label}</Text>
            <div className='mt-2 -mb-1'>
                <Text h3 as='p'>
                    {stat ? formatNumber(String(stat)) : '--'}
                </Text>
            </div>
        </Box>
    )
    if (url) return <Link href={url}>{content}</Link>
    return content
}

const QuickStats = () => {
    const websiteViews = useWebsiteViews() ?? '--'
    const aboutViews = useAboutPageViews() ?? '--'
    const youtubeAnalytics = useYouTubeAnalytics() ?? {
        viewCount: '--',
        videoCount: '--',
        subscriberCount: '--',
    }
    const channelUrl = 'https://www.youtube.com/FinaleSuperuser?sub_confirmation=1'

    return (
        <div>
            <div className='flex flex-col-reverse md:gap-16 md:grid md:grid-cols-2'>
                <div className='grid gap-4 sm:grid-cols-2'>
                    <Stat
                        label='YouTube views'
                        stat={youtubeAnalytics.viewCount}
                        url={channelUrl}
                    />
                    <Stat
                        label='YouTube videos'
                        stat={youtubeAnalytics.videoCount}
                        url={channelUrl}
                    />
                    <Stat
                        label='YouTube subscribers'
                        stat={youtubeAnalytics.subscriberCount}
                        url={channelUrl}
                    />
                    <Stat label='Website views' stat={websiteViews} />
                </div>
                <div className='h-12 md:hidden' />
                <div>
                    <div className='sticky flex flex-col space-y-8 top-32'>
                        <Text h2>Dashboard</Text>
                        <Text>
                            This is my personal dashboard. I use it to track various metrics and
                            goals about my life, all the way from YouTube to Spotify. More stats
                            coming… eventually. Though hooking up the APIs can take some time.
                        </Text>
                        <Text>
                            …oh, and this page has gotten{' '}
                            {aboutViews ? formatNumber(aboutViews.toString()) : '--'} views.
                        </Text>
                    </div>
                </div>
            </div>
        </div>
    )
}

type CurrentlyPlayingProps = {
    listening: boolean
    current: ParsedTrack | undefined
    recent: ParsedTrack[] | undefined
}

const CurrentlyPlaying = ({ listening, current, recent }: CurrentlyPlayingProps) => {
    return (
        <div className='wrapper'>
            <div className='flex items-baseline justify-between'>
                <Text h4 as='h2' color='text-gray-d900'>
                    Spotify
                </Text>
                {listening && (
                    <div className='dark'>
                        <RealTime />
                    </div>
                )}
            </div>

            <div className='flex flex-col pl-4 mt-8 -ml-4 border-l-2 border-success-d md:items-center md:justify-between sm:-ml-8 md:ml-0 sm:pl-8 sm:border-l-4 md:flex-row md:pl-0'>
                <div className='min-w-0 text-center md:mr-12 md:ml-12 xl:ml-24 lg:ml-16 md:text-left'>
                    <Text uppercase small color='text-gray-d600'>
                        {listening ? 'Nick is listening to' : 'Nick last listened to'}
                    </Text>
                    <div className='mt-4'>
                        <Text h2 as='h3' truncate={2} color='text-gray-d900'>
                            {current?.title ?? recent?.[0].title ?? '--'}
                        </Text>
                    </div>
                    <div className='mt-4'>
                        <Text h6 as='p' truncate color='text-gray-d700'>
                            By {current?.artist ?? recent?.[0].artist ?? '--'}
                        </Text>
                    </div>
                    <div className='hidden mt-8 md:block'>
                        <Button
                            value='Listen on Spotify'
                            autoCapitalization={false}
                            color='success'
                            icon={<Spotify />}
                            href={current?.songUrl ?? recent?.[0].songUrl ?? ''}
                        />
                    </div>
                </div>
                <div className='flex-shrink-0 w-full max-w-xs mx-auto mt-4 md:max-w-sm lg:max-w-md xl:max-w-lg md:mx-0 sm:mt-8 md:mt-0'>
                    <Link
                        href={current?.songUrl ?? recent?.[0].songUrl ?? ''}
                        className='block overflow-hidden rounded-lg shadow-md'
                    >
                        <Image
                            src={current?.image ?? recent?.[0].image ?? ''}
                            alt={current?.title ?? recent?.[0].title ?? ''}
                            ratio='1x1'
                        />
                    </Link>
                </div>
            </div>
        </div>
    )
}

type RecentlyPlayedProps = {
    listening: boolean
    current: ParsedTrack | undefined
    recent: ParsedTrack[] | undefined
}

const RecentlyPlayed = ({ listening, current, recent }: RecentlyPlayedProps) => {
    return (
        <>
            <Text h5 as='h4' color='text-gray-d600'>
                Recently played
            </Text>
            {recent && (
                <div
                    className='grid grid-flow-col gap-8 mt-4 overflow-scroll'
                    style={{ scrollSnapType: 'x mandatory' }}
                >
                    {recent.map((track, index: number) => {
                        /* eslint-disable react/jsx-no-useless-fragment -- base cases */
                        if (!listening && index === 0) return <></>
                        if (index === 0 && current?.title === track.title) return <></>
                        if (track.title === recent[index + 1]?.title) return <></>
                        /* eslint-enable react/jsx-no-useless-fragment -- base cases */
                        return (
                            <div
                                style={{ scrollSnapAlign: 'start' }}
                                key={`${track.title}${index}`}
                            >
                                <Link
                                    href={track.songUrl}
                                    className='block sm:w-24 md:w-32 lg:w-40 xl:w-48 hover:text-gray-d800'
                                >
                                    <div className='overflow-hidden rounded-lg shadow-md'>
                                        <Image src={track.image} alt={track.title} ratio='1x1' />
                                    </div>
                                    <div className='mt-4'>
                                        <Text truncate={2}>{track.title}</Text>
                                    </div>
                                </Link>
                            </div>
                        )
                    })}
                </div>
            )}
        </>
    )
}

const TopTracks = ({ tracks }: { tracks: ParsedTrack[] | undefined }) => {
    return (
        <>
            <Text h4 center color='text-gray-d600'>
                Top tracks of all-time
            </Text>
            {tracks?.map((track, index: number) => (
                <SpotifyTrack
                    title={track.title}
                    url={track.songUrl}
                    image={track.image}
                    artist={track.artist}
                    index={index}
                    key={`${track.songUrl}`}
                />
            ))}
        </>
    )
}

export default function About(): JSX.Element {
    const topTracks = useTopTracks()
    const currentlyPlaying = useCurrentlyPlaying()
    const recentlyPlayed = useRecentlyPlayed()

    const isCurrentlyListening = currentlyPlaying && JSON.stringify(currentlyPlaying) !== '{}'

    return (
        <>
            <Head>
                <link rel='preload' href='/api/analytics/youtube-analytics' as='fetch' />
                <link rel='preload' href='/api/analytics/website-analytics' as='fetch' />
                <link rel='preload' href='/api/spotify/currently-playing' as='fetch' />
                <link rel='preload' href='/api/spotify/recently-played' as='fetch' />
            </Head>
            <Layout title='About'>
                <section className='pt-8 pb-16 md:py-24 wrapper'>
                    <Bio />
                </section>
                <section className='py-16 md:py-24 wrapper'>
                    <CoreValues />
                </section>
                <section className='py-16 md:py-24 wrapper'>
                    <QuickStats />
                </section>
                <section id='spotify'>
                    <div className='px-6 pt-16 pb-8 text-gray-d600 bg-gradient-to-b from-gray-800 to-gray-900 dark'>
                        <CurrentlyPlaying
                            listening={isCurrentlyListening ?? false}
                            current={currentlyPlaying}
                            recent={recentlyPlayed}
                        />
                    </div>
                    <div className='px-6 pt-8 pb-12 -mb-8 text-gray-d700 sm:pb-24 lg:pb-32 bg-gradient-to-b from-gray-900 to-black dark'>
                        <div className='hidden wrapper md:block'>
                            <RecentlyPlayed
                                listening={isCurrentlyListening ?? false}
                                current={currentlyPlaying}
                                recent={recentlyPlayed}
                            />
                        </div>
                        <div className='max-w-4xl mx-auto mt-8 lg:mt-24'>
                            <TopTracks tracks={topTracks} />
                        </div>
                    </div>
                </section>
            </Layout>
        </>
    )
}
